import React, { useState, useEffect, useRef } from 'react';
import { RulerDimensionLine, Minus, Plus } from 'lucide-react';

interface VarianceSelectorProps {
    filter: any;
    onChange: (filter: any, range: [number, number]) => void;
    isImperial: boolean;
    formatLabel?: (value: number) => string;
}

export const VarianceSelector: React.FC<VarianceSelectorProps> = ({ filter, onChange, isImperial, formatLabel }) => {
    const minLimit = filter.options[0][0];
    const maxLimit = filter.options[0][1];

    // Determine unit label and type
    const isWeight = filter.id === 'weight';
    const isHeight = filter.id === 'height';
    const unitLabel = isWeight ? (isImperial ? 'lbs' : 'kg') : (isHeight ? (isImperial ? 'ft\'in"' : 'cm') : '');
    const varianceUnitLabel = isWeight ? (isImperial ? 'lbs' : 'kg') : (isHeight ? (isImperial ? 'in' : 'cm') : '');

    // Helper to format height as ft'in"
    const formatHeight = (totalInches: number) => {
        const ft = Math.floor(totalInches / 12);
        const inches = Math.round(totalInches % 12);
        return `${ft}'${inches}"`;
    };

    // Helper to parse height like 8'0" or 5'11"
    const parseHeight = (val: string): number | null => {
        if (/^\d+$/.test(val.trim())) return parseInt(val);
        const match = val.match(/(\d+)'(\d+)?"?/);
        if (match) {
            const ft = parseInt(match[1]);
            const inches = match[2] ? parseInt(match[2]) : 0;
            return ft * 12 + inches;
        }
        return null;
    };

    const isModified = filter.value[0] !== minLimit || filter.value[1] !== maxLimit;

    // State for target and variance
    const [target, setTarget] = useState(() => {
        if (!isModified) return Math.round((minLimit + maxLimit) / 2);
        return Math.round((filter.value[0] + filter.value[1]) / 2);
    });
    const [variance, setVariance] = useState(() => {
        if (!isModified) {
            if (isWeight) return isImperial ? 10 : Math.round(4.536);
            if (isHeight) return isImperial ? 1 : Math.round(2.54);
        }
        return Math.round((filter.value[1] - filter.value[0]) / 2);
    });

    // Local string state for inputs
    const [targetInput, setTargetInput] = useState(() => {
        if (isHeight && isImperial) return formatHeight(target);
        return target.toString();
    });
    const [varianceInput, setVarianceInput] = useState(variance.toString());

    // Sync with external changes
    useEffect(() => {
        const incomingMin = filter.value[0];
        const incomingMax = filter.value[1];

        const isIncomingModified = incomingMin !== minLimit || incomingMax !== maxLimit;

        if (!isIncomingModified) {
            // When cleared or unmodified externally, ensure we reset to default target/variance
            const defaultTarget = Math.round((minLimit + maxLimit) / 2);
            let defaultVariance = 0;
            if (isWeight) defaultVariance = isImperial ? 10 : Math.round(4.536);
            else if (isHeight) defaultVariance = isImperial ? 1 : Math.round(2.54);
            else defaultVariance = Math.round((maxLimit - minLimit) / 2);

            setTarget(defaultTarget);
            setTargetInput(isHeight && isImperial ? formatHeight(defaultTarget) : defaultTarget.toString());
            setVariance(defaultVariance);
            setVarianceInput(defaultVariance.toString());
        } else {
            const currentMin = Math.max(minLimit, target - variance);
            const currentMax = Math.min(maxLimit, target + variance);

            // Only sync if the incoming range differs from our calculated range
            if (incomingMin !== currentMin || incomingMax !== currentMax) {
                const newTarget = Math.round((incomingMin + incomingMax) / 2);
                const newVariance = Math.round((incomingMax - incomingMin) / 2);

                // console.log(`[VarianceSelector] External sync for ${filter.id}:`, { incomingMin, incomingMax, newTarget, newVariance });

                setTarget(newTarget);
                setTargetInput(isHeight && isImperial ? formatHeight(newTarget) : newTarget.toString());
                setVariance(newVariance);
                setVarianceInput(newVariance.toString());
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter.value, minLimit, maxLimit, isHeight, isImperial]);

    const updateParentRange = (newTarget: number, newVariance: number) => {
        const newMin = Math.max(minLimit, newTarget - newVariance);
        const newMax = Math.min(maxLimit, newTarget + newVariance);
        // console.log(`[VarianceSelector] Updating ${filter.id}:`, { newMin, newMax });
        onChange(filter, [newMin, newMax]);
    };

    const handleTargetChange = (val: number) => {
        const clamped = Math.min(maxLimit, Math.max(minLimit, val));
        setTarget(clamped);
        setTargetInput(isHeight && isImperial ? formatHeight(clamped) : clamped.toString());
        updateParentRange(clamped, variance);
    };

    const handleTargetInputChange = (val: string) => {
        setTargetInput(val);
        if (val === '') return;

        const num = isHeight && isImperial ? parseHeight(val) : parseInt(val);
        if (num !== null && !isNaN(num)) {
            // Only update the target if the number is within bounds.
            // If it's out of bounds, we keep the input as-is and wait for blur or more digits.
            if (num >= minLimit && num <= maxLimit) {
                setTarget(num);
                updateParentRange(num, variance);
            }
        }
    };

    const handleTargetBlur = () => {
        setTargetInput(isHeight && isImperial ? formatHeight(target) : target.toString());
    };

    const handleVarianceChange = (val: number) => {
        const clamped = Math.max(0, val);
        setVariance(clamped);
        setVarianceInput(clamped.toString());
        updateParentRange(target, clamped);
    };

    const handleVarianceInputChange = (val: string) => {
        setVarianceInput(val);
        if (val === '') return;
        const num = parseInt(val);
        if (!isNaN(num)) {
            const clamped = Math.max(0, num);
            setVariance(clamped);
            updateParentRange(target, clamped);
        }
    };

    const handleVarianceBlur = () => {
        setVarianceInput(variance.toString());
    };

    const handleClear = () => {
        if (filter.clear) filter.clear();
    };

    const isRangeModified = filter.value[0] !== minLimit || filter.value[1] !== maxLimit;

    const getPercent = (value: number) =>
        ((value - minLimit) / (maxLimit - minLimit)) * 100;

    const targetPercent = getPercent(target) - 3;
    const minPercent = getPercent(Math.max(minLimit, target - (variance/2)));
    const maxPercent = getPercent(Math.min(maxLimit, target + (variance/2)));

    return (
        <div
            className="flex flex-col box-border gap-1 px-3 w-full"
            style={{ maxWidth: '16.25rem', height: 'auto', overflowX: 'visible' }}
        >
            <div className="flex flex-col justify-center items-start gap-1 w-full rounded-sm">

                {isRangeModified && (
                    <div className="flex justify-start w-full mb-1">
                        <button
                            type="button"
                            onClick={handleClear}
                            className="underline tracking-tighter hover:text-gray-900 text-gray-500"
                            style={{ fontSize: '0.625rem', fontWeight: 500 }}
                        >
                            Clear range
                        </button>
                    </div>
                )}


                <div className="relative w-full h-4 mt-1 flex items-center">
                    {/* Background Bar */}
                    <div className="absolute w-full h-1.5 bg-blue-100 rounded-full top-1 -translate-y-1/2" />

                    {/* Range Selection Bar */}
                    <div
                        className="absolute h-1.5 rounded-full top-1 -translate-y-1/2 bg-blue-500"
                        style={{
                            left: `${minPercent}%`,
                            width: `${maxPercent - minPercent}%`,
                            background: 'linear-gradient(270deg, rgba(59, 130, 246, 0.5) 0%, #3b82f6 40.38%, #3b82f6 60%, rgba(59, 130, 246, 0.5) 100%)'
                        }}
                    />

                    {/* Hidden Range Input */}
                    <input
                        type="range"
                        min={minLimit}
                        max={maxLimit}
                        value={target}
                        onChange={(e) => handleTargetChange(Number(e.target.value))}
                        className="absolute w-full h-full opacity-0 cursor-pointer z-30 m-0 p-0 top-0"
                    />

                    {/* Visible Handle */}
                    <div
                        className="absolute top-0 -translate-y-1/2 -translate-x-1/2 w-4 h-4 bg-white border border-blue-500 shadow-md rounded-full pointer-events-none z-20 flex items-center justify-center"
                        style={{ left: `${targetPercent}%`, boxShadow: '0 0 0 0.125rem rgba(59, 130, 246, 0.4)' }}
                    />
                </div>

                <div className="flex flex-row items-start pt-4 gap-2.5 self-stretch" style={{ height: '4.875rem' }}>
                    <div className="flex flex-col items-start gap-1 flex-grow" style={{ height: '3.875rem' }}>
                        <div className="flex flex-row justify-center items-center gap-2.5 self-stretch" style={{ height: '1.0625rem' }}>
                            <label
                                className="flex-grow text-gray-700"
                                style={{ fontSize: '0.6875rem', fontWeight: 500, letterSpacing: '-0.005em', lineHeight: '1rem' }}
                            >
                                Target {unitLabel ? `(${unitLabel})` : ''}
                            </label>
                        </div>
                        <div className="box-border flex flex-row items-start p-1 gap-2.5 self-stretch bg-gray-100 border border-slate-200 rounded-lg" style={{ height: '2.5rem' }}>
                            <div className="flex flex-row items-center px-3 py-1.5 gap-2.5 flex-grow" style={{ height: '2rem' }}>
                                <input
                                    type="text"
                                    value={targetInput}
                                    onChange={(e) => handleTargetInputChange(e.target.value)}
                                    onBlur={handleTargetBlur}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleTargetBlur();
                                        }
                                    }}
                                    className="w-full bg-transparent border-none focus:ring-0 text-sm font-medium text-gray-700 p-0"
                                    style={{ height: '1.3125rem' }}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col items-start gap-1" style={{ width: '5.75rem', height: '3.875rem' }}>
                        <div className="flex flex-row justify-center items-center gap-2.5 self-stretch" style={{ height: '1.0625rem' }}>
                            <label
                                className="flex-grow text-gray-700"
                                style={{ fontSize: '0.6875rem', fontWeight: 500, letterSpacing: '-0.005em', lineHeight: '1rem' }}
                            >
                                Variance {varianceUnitLabel ? `(${varianceUnitLabel})` : ''}
                            </label>
                        </div>

                        <div className="flex flex-row items-center gap-1 w-full" style={{ height: '2.5rem' }}>
                            <div className="box-border flex flex-row items-start p-1 w-full bg-gray-100 border border-slate-200 rounded-lg relative" style={{ height: '2.5rem' }}>
                                <button
                                    type="button"
                                    onClick={() => handleVarianceChange(variance - 1)}
                                    className="flex items-center justify-center hover:bg-blue-50 z-10"
                                    style={{ width: '1.625rem', height: '2rem' }}
                                >
                                    <Minus size={14} className="text-slate-500" strokeWidth={2.5} />
                                </button>
                                <div className="flex flex-row justify-center items-center py-1.5 gap-2.5" style={{ width: '2.5rem', height: '2rem' }}>
                                    <input
                                        type="text"
                                        value={varianceInput}
                                        onChange={(e) => handleVarianceInputChange(e.target.value)}
                                        onBlur={handleVarianceBlur}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleVarianceBlur();
                                            }
                                        }}
                                        className="w-full text-center bg-transparent border-none focus:ring-0 text-sm font-medium text-gray-700 p-0 flex items-center justify-center"
                                        style={{ height: '1.3125rem' }}
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleVarianceChange(variance + 1)}
                                    className="flex items-center justify-center hover:bg-blue-50 z-10"
                                    style={{ width: '1.625rem', height: '2rem' }}
                                >
                                    <Plus size={14} className="text-slate-500" strokeWidth={2.5} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex flex-row items-center gap-1 w-full text-gray-500 mb-2" style={{ fontSize: '0.625rem', fontWeight: 500 }}>
                <RulerDimensionLine size={14} className="text-gray-500" strokeWidth={1} />
                <span>Update units in <a className="underline hover:text-blue-600" href="/auth/edit">your profile</a>.</span>
            </div>
        </div>
    );
};
